import * as Sentry from '@sentry/react';
import { PaymentError } from 'shared/consts/payment-error-types';

interface LogCartSubmissionErrorContext {
  cartId?: string;
  paymentMethodId?: string;
}

/**
 * Reports cart submission (`POST /api/payments/v1/carts/{id}`) failures to
 * Sentry with the full error context.
 *
 * The UI collapses every submit failure into the generic "cart_submission_error"
 * message (it's the default branch of getCartErrorMessage), so we can't tell
 * from the user's screen whether it was a real gateway/API error or a
 * network/client-side failure that never reached the backend. Capturing the
 * HTTP status, error key and whether a response was received closes that gap.
 * See SMB-929.
 */
export const logCartSubmissionError = (error: PaymentError, context: LogCartSubmissionErrorContext = {}): void => {
  const httpStatus = error?.status ?? error?.response?.statusCode;
  const errorKey = error?.response?.message?.key;
  // A missing response usually means the request never got a reply
  // (network error, CORS, timeout, blocked request) rather than an API error.
  const hasApiResponse = Boolean(error?.response);

  Sentry.captureException(error, {
    level: 'error',
    tags: {
      feature: 'checkout',
      operation: 'submit-cart',
      error_key: errorKey ?? 'unknown',
      has_api_response: hasApiResponse,
    },
    extra: {
      cartId: context.cartId,
      paymentMethodId: context.paymentMethodId,
      httpStatus,
      errorKey,
      errorMessage: error?.response?.message?.message ?? error?.message,
      path: error?.response?.path,
      hasApiResponse,
    },
  });
};
